import type { SVGAttributes } from 'react';
import { cn } from './cn';

export interface LogoProps extends Omit<
  SVGAttributes<SVGSVGElement>,
  'children'
> {
  /** Rendered width and height in px. */
  size?: number;
  /**
   * `mark`: the target glyph in the current text color (header, on surfaces).
   * `tile`: white glyph on an accent rounded square (app icon, favicon).
   */
  variant?: 'mark' | 'tile';
  /** Accessible name. Omit when the logo sits next to the visible app name. */
  title?: string;
}

/**
 * Resolve mark: a quartered survey target inside a reticle. Geometry is
 * shared with public/favicon.svg; it stays legible down to 16px.
 */
export const Logo = ({
  size = 20,
  variant = 'mark',
  title,
  className,
  ...props
}: LogoProps) => {
  const a11y = title
    ? ({ role: 'img', 'aria-label': title } as const)
    : ({ 'aria-hidden': true } as const);

  if (variant === 'tile') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 32 32"
        fill="none"
        className={cn('shrink-0', className)}
        {...a11y}
        {...props}
      >
        <rect width="32" height="32" rx="7" className="fill-accent" />
        <g className="fill-accent-fg stroke-accent-fg">
          <circle cx="16" cy="16" r="7.5" fill="none" strokeWidth="2.25" />
          <path d="M16 16V11.25A4.75 4.75 0 0 1 20.75 16Z" stroke="none" />
          <path d="M16 16V20.75A4.75 4.75 0 0 1 11.25 16Z" stroke="none" />
          <path
            d="M16 3.25V6.5M16 25.5V28.75M3.25 16H6.5M25.5 16H28.75"
            fill="none"
            strokeWidth="2.25"
            strokeLinecap="round"
          />
        </g>
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      className={cn('shrink-0', className)}
      {...a11y}
      {...props}
    >
      <circle cx="16" cy="16" r="9.5" stroke="currentColor" strokeWidth="2.5" />
      <path d="M16 16V10A6 6 0 0 1 22 16Z" fill="currentColor" />
      <path d="M16 16V22A6 6 0 0 1 10 16Z" fill="currentColor" />
      <path
        d="M16 1.75V5.5M16 26.5V30.25M1.75 16H5.5M26.5 16H30.25"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
};

export interface LogoLockupProps {
  /** Mark size in px. */
  size?: number;
  /** Show the "by Cadastre" byline. */
  showByline?: boolean;
  /** Classes for the byline, e.g. `hidden sm:inline` to drop it on phones. */
  bylineClassName?: string;
  className?: string;
}

/**
 * Brand lockup for the top bar: accent mark, "Resolve" (15/600), and a
 * small muted "by Cadastre". Reads as "Resolve by Cadastre".
 */
export const LogoLockup = ({
  size = 22,
  showByline = true,
  bylineClassName,
  className,
}: LogoLockupProps) => (
  <span className={cn('inline-flex items-center gap-2', className)}>
    <Logo size={size} className="text-accent" />
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="text-[15px] font-semibold tracking-tight text-fg">
        Resolve
      </span>
      {showByline && (
        <span
          className={cn('text-xs font-medium text-fg-muted', bylineClassName)}
        >
          by Cadastre
        </span>
      )}
    </span>
  </span>
);
